import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Footer } from "@/components/Footer";

const INCLUDED = [
  "Unlimited conversations",
  "Daily reflection emails",
  "Journey exports",
  "Your full history, always kept",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const isMember = user && user !== false && user.subscription_status === "active";

  const startCheckout = async () => {
    if (loading) return;
    if (!user || user === false) {
      navigate("/auth");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 px-6 sm:px-10 py-8">
        <header className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            data-testid="pricing-back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 text-sm"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <span className="text-xs tracking-[0.35em] uppercase text-[var(--mirror-text-2)]">
            Membership
          </span>
        </header>

        <main className="max-w-lg mx-auto mt-20 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight"
          >
            Keep looking.
          </motion.h1>
          <p className="mt-5 text-[var(--mirror-text-2)] leading-relaxed">
            Your first reflection is yours, freely. Become a member to continue the conversation
            with yourself — as often as you need.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-12 rounded-2xl border border-[var(--mirror-border)] p-8 text-left"
            data-testid="pricing-card"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-4xl font-light text-[var(--mirror-text)]">
                $9
              </span>
              <span className="text-[var(--mirror-text-3)] text-sm">/ month</span>
            </div>

            <ul className="mt-8 space-y-4">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[var(--mirror-text-2)]">
                  <Check size={16} className="mt-1 shrink-0 text-[var(--mirror-accent)]" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {isMember ? (
              <div
                data-testid="pricing-already-member"
                className="mt-10 w-full text-center py-3 rounded-full border border-[var(--mirror-border)] text-sm tracking-[0.2em] uppercase text-[var(--mirror-text-3)]"
              >
                You are a member
              </div>
            ) : (
              <button
                data-testid="pricing-checkout-button"
                onClick={startCheckout}
                disabled={loading}
                className="mt-10 w-full py-3.5 rounded-full bg-[var(--mirror-accent)] text-[#16151F] text-sm tracking-[0.2em] uppercase font-medium hover:opacity-90 transition-opacity duration-500 disabled:opacity-50"
              >
                {loading ? "Opening checkout…" : "Become a member"}
              </button>
            )}

            <p className="mt-6 text-center text-xs text-[var(--mirror-text-3)] leading-relaxed">
              Cancel anytime. By subscribing you agree to our{" "}
              <Link to="/terms" className="underline underline-offset-2 hover:text-[var(--mirror-text)]">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline underline-offset-2 hover:text-[var(--mirror-text)]">
                Privacy Policy
              </Link>
              .
            </p>
          </motion.div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
